import React, { useState, useContext, useEffect } from "react";
import {
  Modal,
  Input,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Upload,
  Form,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { RcFile } from "antd/es/upload";
import { UploadFile } from "antd/lib/upload";
import { AuthContext } from "../../Auth/AuthContext";
import { MdDeleteOutline } from "react-icons/md";
import { getAllSources, getAllTags, getContactsById, updateContacts } from "../../utils/API";
import dayjs from "dayjs";

const { Option } = Select;

interface Source {
  _id: string;
  name: string;
  slug?: string;
  category?: string[];
}

interface Tag {
  _id: string;
  name: string;
  color?: string;
  category?: string[];
}

interface EditContactModalProps {
  visible: boolean;
  onCancel: () => void;
  customerId: string;
  onSuccess?: () => void;
  onCreate: (values: any) => void;
  onUpdate: (values: any) => Promise<void>;
  initialValues: any;
  refreshContacts: () => void;
  sources?: Source[];
  tags?: Tag[];
}

const Editcontactmodal: React.FC<EditContactModalProps> = ({
  visible,
  onCancel,
  onCreate,
  onSuccess,
  onUpdate,
  initialValues,
  customerId,
  refreshContacts,
  sources: propsSources = [],
  tags: propsTags = [],
}) => {
  const [form] = Form.useForm();
  const [customerData, setCustomerData] = useState<any>(null);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string>("");
  const { authData } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const roleId = authData.roleId;
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const IMAGEURL = process.env.REACT_APP_IMAGE_URL + "/contacts/";
  const [sources, setSources] = useState<Source[]>(propsSources);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [tags, setTags] = useState<Tag[]>(propsTags);
  const [tagsLoading, setTagsLoading] = useState(false);

  // Create lookup objects for ID to name conversion
  const sourceLookup = sources.reduce((acc: Record<string, string>, source) => {
    acc[source._id] = source.name;
    return acc;
  }, {});

  const tagLookup = tags.reduce((acc: Record<string, { name: string; color?: string }>, tag) => {
    acc[tag._id] = { name: tag.name, color: tag.color };
    return acc;
  }, {});

  // Reverse lookup: name to ID
  const sourceNameToId = sources.reduce((acc: Record<string, string>, source) => {
    acc[source.name] = source._id;
    return acc;
  }, {});

  const tagNameToId = tags.reduce((acc: Record<string, string>, tag) => {
    acc[tag.name] = tag._id;
    return acc;
  }, {});

  const fetchCustomer = async () => {
    if (!customerId) return;

    setIsLoading(true);
    try {
      const data = await getContactsById(customerId);
      setCustomerData(data);

      // Convert IDs to names for form display
      const leadSourceName = data.leadSource ? sourceLookup[data.leadSource] || data.leadSource : "";
      const tagNames = data.tags?.map((tagId: string) => tagLookup[tagId]?.name || tagId) || [];

      // Set form values including lead status
      form.setFieldsValue({
        contactName: data.contactName || data.name,
        phone: data.phone || data.mobile,
        company: data.company || data.companyName,
        email: data.email,
        score: data.score,
        leadStatus: data.leadStatus || "New",
        leadSource: leadSourceName, // Set name instead of ID
        tags: tagNames, // Set names instead of IDs
        LastContacted: data.updatedAt ? dayjs(data.updatedAt) : null,
      });

      if (data.profilePhoto) {
        setPreviewImage(IMAGEURL + data.profilePhoto);
        setFileList([
          {
            uid: "1",
            name: "profilePhoto",
            status: "done",
            url: IMAGEURL + data.profilePhoto,
          },
        ]);
      }
    } catch (error) {
      message.error("Failed to fetch customer data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (visible && customerId) {
      fetchCustomer();
    }
  }, [visible, customerId]);

  // If props are passed, use them; otherwise fetch
  useEffect(() => {
    if (visible && (propsSources.length === 0 || propsTags.length === 0)) {
      fetchSources();
      fetchTags();
    }
  }, [visible]);

  const handleSubmit = async (values: any) => {
    if (!customerId) {
      message.error("Customer ID is missing!");
      return;
    }

    // Validate email or phone
    const email = values.email ? values.email.trim() : "";
    const phone = values.phone ? values.phone.trim() : "";

    if (!email && !phone) {
      message.error("Please provide either email or phone number");
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append("id", customerId);

      // Convert names back to IDs for leadSource
      if (values.leadSource) {
        const sourceId = sourceNameToId[values.leadSource] || values.leadSource;
        formData.append("leadSource", sourceId);
      }

      // Convert tag names back to IDs
      if (values.tags && Array.isArray(values.tags)) {
        values.tags.forEach((tagName: string) => {
          const tagId = tagNameToId[tagName] || tagName;
          formData.append("tags", tagId);
        });
      }

      // Append other form values
      const otherFields = ['contactName', 'phone', 'company', 'email', 'score', 'leadStatus', 'LastContacted'];
      otherFields.forEach((key) => {
        if (values[key] !== undefined && values[key] !== null) {
          if (key === 'LastContacted' && values[key]) {
            // Format date properly
            const date = dayjs(values[key]);
            formData.append(key, date.toISOString());
          } else {
            formData.append(key, values[key]);
          }
        }
      });

      // Append profile photo if it exists
      if (profilePhoto) {
        formData.append("profilePhoto", profilePhoto);
      } else if (previewImage && previewImage.startsWith("data:")) {
        // Handle case where new image was selected but not properly captured in profilePhoto
        const blob = await fetch(previewImage).then((r) => r.blob());
        const file = new File([blob], "profile.jpg", { type: blob.type });
        formData.append("profilePhoto", file);
      }

      await updateContacts(formData);
      message.success("Lead updated successfully!");
      if (onSuccess) onSuccess();
      refreshContacts();
      onCancel();
    } catch (error) {
      message.error("Failed to update lead.");
      console.error("Update error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const getBase64 = (file: File | Blob) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  const imageProps = {
    name: "profilePhoto",
    multiple: false,
    beforeUpload(file: RcFile) {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("You can only upload image files!");
        return Upload.LIST_IGNORE;
      }
      const isLt64M = file.size / 1024 / 1024 < 64;
      if (!isLt64M) {
        message.error("Image must be smaller than 64MB!");
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onChange: async (info: any) => {
      const newFileList = [...info.fileList].slice(-1); // Only keep latest file

      if (newFileList.length > 0) {
        const file = newFileList[0];

        if (file.originFileObj) {
          const preview = await getBase64(file.originFileObj);
          file.preview = preview;
          setPreviewImage(preview);
          setProfilePhoto(file.originFileObj);
        } else {
          setPreviewImage(file.url || file.preview || "");
        }
      } else {
        setPreviewImage("");
        setProfilePhoto(null);
      }

      setFileList(newFileList);
    },
    fileList,
  };

  const fetchSources = async () => {
    try {
      setSourcesLoading(true);
      const res = await getAllSources("", 1);

      // Frontend filtering
      const filteredSources = (res.sources || []).filter(
        (source: any) =>
          Array.isArray(source.category) &&
          source.category.includes("Contacts"),
      );

      setSources(filteredSources);
    } catch (err) {
      console.error("Failed to fetch sources", err);
      message.error("Failed to load lead sources");
    } finally {
      setSourcesLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      setTagsLoading(true);
      const res = await getAllTags();
      const allTags = res.tags || res || [];

      // Filter only Contacts tags
      const contactTags = allTags.filter(
        (tag: any) =>
          Array.isArray(tag.category) && tag.category.includes("Contacts"),
      );

      setTags(contactTags);
    } catch (err) {
      console.error("Failed to fetch tags", err);
      message.error("Failed to load tags");
    } finally {
      setTagsLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      centered={false}
      destroyOnClose
      className="custom-modal"
      style={{
        bottom: 20,
        top: 20,
        maxHeight: "calc(100vh - 30px)",
        margin: "0 auto",
        position: "relative",
      }}
      bodyStyle={{
        overflowY: "auto",
        fontFamily: "inter, sans-serif",
      }}
      styles={{
        header: {
          padding: "12px",
          position: "sticky",
          top: 0,
          backgroundColor: "#f1f5ffff",
        },
        content: {
          padding: 0,
          overflow: "hidden",
        },
      }}
      title={
        <div
          style={{
            color: "#4b5563",
            fontFamily: "inter, sans-serif",
            fontSize: "20px",
            fontWeight: 500,
          }}
        >
          Edit Contacts
        </div>
      }
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 19 }}
        colon={false}
        style={{ margin: 0 }}
      >
        <div className="elements">
          <Form.Item
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            className="w-full"
          >
            <div className="flex justify-center ">
              <div className="relative w-20 h-20">
                <Upload.Dragger
                  {...imageProps}
                  className="w-full h-full rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-blue-500 transition-colors bg-gray-50 overflow-hidden"
                  showUploadList={false}
                >
                  {!previewImage ? (
                    <div className="flex flex-col items-center p-1">
                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                        <UploadOutlined className="text-gray-400 text-base" />
                      </div>
                      <span className="text-xs text-gray-400 mt-1">
                        Profile
                      </span>
                    </div>
                  ) : (
                    <img
                      src={previewImage}
                      alt="Uploaded"
                      className="w-full h-full object-cover"
                    />
                  )}
                </Upload.Dragger>

                {previewImage && (
                  <Button
                    type="text"
                    shape="circle"
                    size="small"
                    icon={<MdDeleteOutline size={16} />}
                    className="absolute top-0 right-0 bg-red-500 text-white hover:bg-red-600 shadow-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewImage("");
                      setFileList([]);
                      setProfilePhoto(null);
                    }}
                  />
                )}
              </div>
            </div>
          </Form.Item>

          <Form.Item
            name="contactName"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Contact Name <span className="text-danger">*</span>
              </span>
            }
            rules={[{ required: true, message: "Please enter contact name" }]}
            required={false}
          >
            <Input
              size="large"
              placeholder="Enter Contact Name"
              className="Allinput"
            />
          </Form.Item>

          <Form.Item
            name="score"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Lead Score
              </span>
            }
          >
            <Input
              size="large"
              type="number"
              placeholder="Enter Lead Score"
              className="Allinput"
            />
          </Form.Item>

          <Form.Item
            name="email"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Email
              </span>
            }
            rules={[
              {
                type: "email",
                message: "Invalid email format",
              },
              // Add validator to check email or phone
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const phone = getFieldValue("phone");
                  if (
                    (!value || value.trim() === "") &&
                    (!phone || phone.trim() === "")
                  ) {
                    return Promise.reject(
                      new Error("Please provide either email or phone number"),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            validateTrigger={["onChange", "onBlur"]}
          >
            <Input
              size="large"
              placeholder="Enter Email"
              className="Allinput"
            />
          </Form.Item>

          <Form.Item
            name="phone"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Phone Number
              </span>
            }
            rules={[
              // Add validator to check email or phone
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const email = getFieldValue("email");
                  if (
                    (!value || value.trim() === "") &&
                    (!email || email.trim() === "")
                  ) {
                    return Promise.reject(
                      new Error("Please provide either email or phone number"),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            validateTrigger={["onChange", "onBlur"]}
          >
            <Input
              size="large"
              placeholder="Enter phone Number"
              className="Allinput"
            />
          </Form.Item>

          <Form.Item
            name="company"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Company Name
              </span>
            }
          >
            <Input
              size="large"
              placeholder="Enter Company Name"
              className="Allinput"
            />
          </Form.Item>

          <Form.Item
            name="leadSource"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Lead Source
              </span>
            }
          >
            <Select
              size="large"
              placeholder="Select Lead Source"
              loading={sourcesLoading}
              allowClear
              className="CustomSelect"
              optionLabelProp="label"
            >
              {sources.map((source) => (
                <Option key={source._id} value={source.name} label={source.name}>
                  {source.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="LastContacted"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Last Contacted
              </span>
            }
            required={false}
          >
            <DatePicker
              format="DD-MM-YYYY HH:mm"
              size="large"
              showTime
              className="Allinput"
            />
          </Form.Item>

          <Form.Item name="tags" label="Tags">
            <Select
              allowClear
              mode="multiple"
              size="large"
              disabled={roleId === 2}
              placeholder="Select Tags"
              className="CustomSelect"
              loading={tagsLoading}
              optionLabelProp="label"
            >
              {tags.map((tag: Tag) => (
                <Option key={tag._id} value={tag.name} label={tag.name}>
                  <span
                    style={{
                      backgroundColor: tag.color || "#f3f4f6",
                      color: "#111827",
                      padding: "2px 8px",
                      borderRadius: "6px",
                      fontSize: "12px",
                    }}
                  >
                    {tag.name}
                  </span>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <Row justify="end">
          <Col
            xs={24}
            sm={24}
            md={24}
            xl={24}
            xxl={20}
            className="d-flex justify-content-end me-3"
          >
            <div className="button-wrapper">
              <button
                className="allcanclebutton"
                onClick={onCancel}
                disabled={isLoading || isSaving}
                type="button"
              >
                Cancel
              </button>
              <button
                className="allsubmitebutton"
                type="submit"
                disabled={isLoading || isSaving}
              >
                {isSaving ? "Updating..." : "Update"}
              </button>
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default Editcontactmodal;